import { useLayoutEffect, useRef, useState } from 'react';
import { niceTicks, compact, thousands, labelStep } from '../data/format.js';

const HEIGHT = 260;
const MARGIN = { top: 12, right: 20, bottom: 30, left: 64 };

function pathOf(rows, key, x, y) {
  let d = '';
  let open = false;
  rows.forEach((row, i) => {
    const value = row[key];
    if (typeof value !== 'number') { open = false; return; }
    d += `${open ? 'L' : 'M'}${x(i).toFixed(1)},${y(value).toFixed(1)}`;
    open = true;
  });
  return d;
}

/**
 * A line chart with one axis. All series must have the same unit.
 * The hover layer is a crosshair that snaps to the nearest year; the arrow keys
 * move it when the chart has focus. The table next to the chart holds each value.
 */
export default function LineChart({ rows, xKey, series, label }) {
  const wrapRef = useRef(null);
  const [width, setWidth] = useState(640);
  const [hover, setHover] = useState(null);

  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const observer = new ResizeObserver(([entry]) => setWidth(Math.max(280, Math.floor(entry.contentRect.width))));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const values = rows.flatMap((row) => series.map((s) => row[s.key])).filter((v) => typeof v === 'number');
  if (rows.length === 0 || values.length === 0) {
    return <p className="muted">No amounts to draw.</p>;
  }

  const ticks = niceTicks(Math.min(0, ...values), Math.max(0, ...values));
  const lo = ticks[0];
  const hi = ticks[ticks.length - 1];
  const innerW = width - MARGIN.left - MARGIN.right;
  // The latest year always has a label; the older years get one every `step` years.
  const step = labelStep(rows.length, innerW);
  const innerH = HEIGHT - MARGIN.top - MARGIN.bottom;
  const x = (i) => MARGIN.left + (rows.length === 1 ? innerW / 2 : (i * innerW) / (rows.length - 1));
  const y = (v) => MARGIN.top + innerH - ((v - lo) / (hi - lo)) * innerH;

  function indexAt(clientX) {
    const box = wrapRef.current.getBoundingClientRect();
    const px = ((clientX - box.left) / box.width) * width;
    if (rows.length === 1) return 0;
    const i = Math.round(((px - MARGIN.left) / innerW) * (rows.length - 1));
    return Math.min(rows.length - 1, Math.max(0, i));
  }

  function onKeyDown(event) {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    event.stopPropagation();
    const step = event.key === 'ArrowRight' ? 1 : -1;
    setHover((h) => Math.min(rows.length - 1, Math.max(0, (h ?? (step > 0 ? -1 : rows.length)) + step)));
  }

  const tipLeft = hover === null ? 0 : Math.min(Math.max(x(hover) + 12, 0), width - 190);
  const tipFlip = hover !== null && x(hover) + 200 > width;

  return (
    <div className="chart">
      <ul className="legend">
        {series.map((s) => (
          <li key={s.key}><span className="line-key" style={{ background: s.color }} />{s.label}</li>
        ))}
      </ul>
      <div ref={wrapRef} style={{ position: 'relative' }}>
        <svg
          viewBox={`0 0 ${width} ${HEIGHT}`}
          height={HEIGHT}
          role="img"
          aria-label={label}
          tabIndex={0}
          onPointerMove={(e) => setHover(indexAt(e.clientX))}
          onPointerLeave={() => setHover(null)}
          onKeyDown={onKeyDown}
          onBlur={() => setHover(null)}
        >
          <g className="grid">
            {ticks.map((t) => <line key={t} x1={MARGIN.left} x2={width - MARGIN.right} y1={y(t)} y2={y(t)} />)}
          </g>
          <line className="zero" x1={MARGIN.left} x2={width - MARGIN.right} y1={y(0)} y2={y(0)} />
          {ticks.map((t) => (
            <text key={t} className="tick" x={MARGIN.left - 10} y={y(t)} dy="0.32em" textAnchor="end">{compact(t)}</text>
          ))}
          {rows.map((row, i) => ((rows.length - 1 - i) % step === 0 && (
            <text key={row[xKey]} className="tick" x={x(i)} y={HEIGHT - 8} textAnchor="middle">{row[xKey]}</text>
          )))}
          {hover !== null && <line className="crosshair" x1={x(hover)} x2={x(hover)} y1={MARGIN.top} y2={MARGIN.top + innerH} />}
          {series.map((s) => (
            <path key={s.key} className="series" d={pathOf(rows, s.key, x, y)} style={{ stroke: s.color }} />
          ))}
          {series.map((s) => rows.map((row, i) => (typeof row[s.key] === 'number' ? (
            <circle key={`${s.key}-${i}`} className="dot" cx={x(i)} cy={y(row[s.key])} r={hover === i ? 5 : 4} style={{ fill: s.color }} />
          ) : null)))}
        </svg>
        {hover !== null && (
          <div className="chart-tip" style={tipFlip ? { left: Math.max(0, x(hover) - 202) } : { left: tipLeft }}>
            <div className="tip-title">{rows[hover][xKey]}</div>
            {series.map((s) => (
              <div className="tip-row" key={s.key}>
                <span className="line-key" style={{ background: s.color }} />
                <span>{s.label}</span>
                <strong>{typeof rows[hover][s.key] === 'number' ? thousands(rows[hover][s.key]) : '—'}</strong>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
